import * as yup from 'yup';

import { languageEnabledArray, localizableString, slug } from '@/helpers/validation.helper';
import type { Locales, ConsultationDto } from '@/services/types/api.types';
import { Constants } from '@/constants';

type ConsultationValidationSchemaOptions = {
  languageEnabled: Locales[];
  communityLanguageEnabled: Locales[];
  isTemplate: boolean;
};

export const consultationValidationSchema = (options: ConsultationValidationSchemaOptions) => {
  if (options.isTemplate) {
    return yup.object<ConsultationDto>({
      title: localizableString(options.languageEnabled),
      slug: slug(),
      description: localizableString(options.languageEnabled),
      open_graph_file: yup
        .object()
        .shape({
          url: yup.string().optional(),
          altText: localizableString(options.languageEnabled, { optional: true }),
        })
        .nullable(),
      banner_file: yup
        .object()
        .shape({
          url: yup.string().optional(),
          altText: localizableString(options.languageEnabled, { optional: true }),
        })
        .nullable(),
      language_enabled: languageEnabledArray(options.communityLanguageEnabled),
      conclusion_message: yup.object().shape({
        enabled: yup.boolean().required(),
        content: yup.mixed().when('enabled', {
          is: true,
          then: () => localizableString(options.languageEnabled),
          otherwise: () => localizableString(options.languageEnabled, { nullable: true, optional: true }),
        }),
      }),
    });
  } else {
    return yup.object<ConsultationDto>({
      title: localizableString(options.languageEnabled),
      slug: slug(),
      description: localizableString(options.languageEnabled),
      status: yup.number().required(),
      begin_date: yup.string().when('options.automatic_publish', {
        is: true,
        then: (schema) => schema.required('Begin date is required when automatic publish is enabled'),
        otherwise: (schema) => schema.nullable(),
      }),
      domains: yup.string().nullable(),
      private_email_allowed: yup
        .object()
        .shape({
          // Not `.required()`: the API always supplies these arrays, so the rule can only ever
          // fire while the form is still empty during load. Its stale async result then sticks,
          // because neither path has a mounted field that would clear it, which permanently
          // disables Save. Empty arrays pass `.required()` anyway, so nothing is lost here.
          email: yup.array().of(yup.string().matches(Constants.EmailRegex)),
          domain: yup.array().of(yup.string()),
        })
        .required(),
      open_graph_file: yup
        .object()
        .shape({
          url: yup.string().optional(),
          altText: localizableString(options.languageEnabled, { optional: true }),
        })
        .nullable(),
      banner_file: yup
        .object()
        .shape({
          url: yup.string().optional(),
          altText: localizableString(options.languageEnabled, { optional: true }),
        })
        .nullable(),
      language_enabled: languageEnabledArray(options.communityLanguageEnabled),
      conclusion_message: yup.object().shape({
        enabled: yup.boolean().required(),
        content: yup.mixed().when('enabled', {
          is: true,
          then: () => localizableString(options.languageEnabled),
          otherwise: () => localizableString(options.languageEnabled, { nullable: true, optional: true }),
        }),
      }),
    });
  }
};
